'use client'

import { interpolateContent, type PriceChartData } from '@/lib/c2c/transforms/price-history'

import { Chart } from './chart/chart'

interface PriceHistoryChartProps {
    chartData: PriceChartData
    cheapestTicketsTitle?: string
    cheapestTicketsDescription?: string
    priceChartSectionTitle?: string
    priceDayTitle?: string
    priceDayDescription?: string
    priceMonthTitle?: string
    priceMonthDescription?: string
    priceDateTitle?: string
    priceDateDescription?: string
}

export function PriceHistoryChart({
    chartData,
    cheapestTicketsTitle,
    cheapestTicketsDescription,
    priceChartSectionTitle,
    priceDayTitle,
    priceDayDescription,
    priceMonthTitle,
    priceMonthDescription,
    priceDateTitle,
    priceDateDescription,
}: PriceHistoryChartProps) {
    const { interpolationValues } = chartData
    const hasPricingInfo = Object.keys(interpolationValues).length > 0

    const sectionTitle = hasPricingInfo ? cheapestTicketsTitle : priceChartSectionTitle

    return (
        <section aria-labelledby="price-chart-header" className="my-[72px] md:my-24">
            {sectionTitle && (
                <h2
                    id="price-chart-header"
                    className="pb-4 text-[20px] font-bold text-[#44474a] md:text-2xl"
                >
                    {sectionTitle}
                </h2>
            )}

            {hasPricingInfo && cheapestTicketsDescription && (
                <p className="mb-6 text-[#44474a]">
                    {interpolateContent(cheapestTicketsDescription, interpolationValues)}
                </p>
            )}

            <div className="flex flex-col">
                {chartData.daily && (
                    <>
                        <Chart
                            data={chartData.daily}
                            title={priceDayTitle}
                            numVerticalTicks={3}
                            xTickValues={chartData.xTickValuesDaily}
                        />
                        {hasPricingInfo && priceDayDescription && (
                            <p className="mb-6 text-[#44474a]">
                                {interpolateContent(priceDayDescription, interpolationValues)}
                            </p>
                        )}
                    </>
                )}

                <div className="flex flex-col md:flex-row">
                    {chartData.monthly && (
                        <div className="flex flex-col md:basis-1/2">
                            <Chart
                                data={chartData.monthly}
                                title={priceMonthTitle}
                                xTickValues={chartData.xTickValuesMonthly}
                            />
                            {hasPricingInfo && priceMonthDescription && (
                                <p className="mb-6 text-[#44474a]">
                                    {interpolateContent(priceMonthDescription, interpolationValues)}
                                </p>
                            )}
                        </div>
                    )}

                    {chartData.daysUntilDepart && (
                        <div className="flex flex-col md:ml-6 md:basis-1/2 md:first:ml-0">
                            <Chart
                                chartType="curve"
                                data={chartData.daysUntilDepart}
                                labelType="number"
                                title={priceDateTitle}
                                xTickValues={chartData.xTickValuesDepartDays}
                            />
                            {hasPricingInfo && priceDateDescription && (
                                <p className="mb-6 text-[#44474a]">
                                    {interpolateContent(priceDateDescription, interpolationValues)}
                                </p>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </section>
    )
}
